import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { HTMLAttributes } from "react";
import { useRecoilValue } from "recoil";

import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { IClip, TClip } from "MVVM/@types";
import { LoadingClipId } from "MVVM/State/clip";
import { LoadingSpinner } from "MVVM/Components/Lottie";

type FeedClip = (TClip | IClip) & {
  thumbnailUrl?: string;
  thumbnailOverlayUrl?: string;
  thumbnailStandardUrl?: string;
  shareId: string;
};

interface IFeedItemProps extends HTMLAttributes<HTMLDivElement> {
  clip: FeedClip;
  active?: boolean;
}

const FeedItem = ({ clip, active, ...rest }: IFeedItemProps) => {
  const loadingClipId = useRecoilValue(LoadingClipId);
  const isLoading = loadingClipId === clip.shareId;

  if (!clip) return <></>;

  return (
    <FeedItemContainer active={active} {...rest}>
      <Thumbnail
        active={active}
        style={{
          backgroundImage: `url(${
            clip?.thumbnailOverlayUrl ||
            clip?.thumbnailStandardUrl ||
            clip?.thumbnailUrl
          })`,
        }}
      >
        {isLoading && (
          <LoadingOverlay>
            <LoadingSpinner width={32} height={32} />
          </LoadingOverlay>
        )}
        {active && (
          <CurrentIndicator>
            <Text fontSize={0} fontWeight={600} color="ice">
              NOW PLAYING
            </Text>
          </CurrentIndicator>
        )}
      </Thumbnail>
      <Flex
        flexDirection="column"
        justifyContent={["space-around", "space-around", "space-evenly"]}
        style={{ gap: 2, opacity: active ? 0.5 : 1 }}
      >
        <Text fontWeight={500}>{clip.title}</Text>
        <Text fontWeight={500} fontSize={1} color="chalk">
          {clip?.username || clip?.user?.username}
        </Text>
        <Flex alignItems="center">
          {!!clip.views && clip.views >= 5 && (
            <>
              <Text fontWeight={500} fontSize={1} color="chalk">
                {clip.views} views
              </Text>
              <Text fontWeight={500} fontSize={1} color="chalk" px={2}>
                &bull;
              </Text>
            </>
          )}
          <Text fontWeight={500} fontSize={1} color="chalk">
            {dateFormatWithDistanceSuffix(clip.createdAt)}
          </Text>
        </Flex>
      </Flex>
    </FeedItemContainer>
  );
};

const FeedItemContainer = styled(Flex)<{ active?: boolean }>`
  color: reset;
  cursor: ${({ active }) => (active ? "default" : "pointer")};
  gap: 18px;
  margin-bottom: 4px;
  overflow: hidden;
  padding: 10px;
  width: 100%;

  ${({ active, theme }) =>
    !active &&
    `
    :hover {
      background: ${theme.colors.navy};
      border-radius: 8px;
    }
  `}

  ${({ active, theme }) =>
    active &&
    `
    background: ${theme.colors.navy600};
    border-radius: 8px;
    border: 1px solid ${theme.colors.ice}40;
  `}

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-direction: column;
  }
`;

const Thumbnail = styled(Box)<{ active?: boolean }>`
  background-size: cover;
  border-radius: 8px;
  cursor: ${({ active }) => (active ? "default" : "pointer")};
  flex-shrink: 0;
  height: 90px;
  position: relative;
  width: 160px;

  ${({ active }) =>
    active &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 8px;
      z-index: 1;
    }
  `}

  @media (max-width: ${({ theme }) => theme.breaks.theater}) {
    aspect-ratio: 16/9;
    height: inherit;
    width: 25%;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    aspect-ratio: 16/9;
    flex-grow: 1;
    height: inherit;
    width: 100%;
  }
`;

const LoadingOverlay = styled(Flex)`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const CurrentIndicator = styled(Flex)`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  bottom: 4px;
  justify-content: center;
  left: 4px;
  padding: 2px 6px;
  position: absolute;
  z-index: 2;
`;

export { FeedItem };
